import { useState, useEffect } from "react";
import { Box, FormControl, MenuItem, Select, Typography, Grid } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { IDefaultRouteCostsData, IVehicleTypeResponse } from "../types";
import { toast } from "react-toastify";
import { ITransshipPoint } from "interfaces/IWaybill";
import { CostLineCard } from "./CostLineCard";
import { NewButton } from "components/NewButton";
import { validateForm } from '../utils/validation';

interface ValidationErrors {
  transshipPointId?: boolean;
  costs: Array<{
    vehicleTypeId?: boolean;
    initialCapacity?: boolean;
    finalCapacity?: boolean;
    overlapError?: boolean;
    cost?: boolean;
    additionalDelivery?: boolean;
  }>;
}

interface NewDefaultCostFormProps {
  form: IDefaultRouteCostsData;
  onSave: (form: IDefaultRouteCostsData) => void;
  onRemove: () => void;
  loading: boolean;
  vehicleTypes: IVehicleTypeResponse[];
  transshipPoints: ITransshipPoint[];
}

const NewDefaultCostForm = ({
  form: initialForm,
  onSave,
  onRemove,
  loading,
  vehicleTypes,
  transshipPoints,
}: NewDefaultCostFormProps) => {
  const [form, setForm] = useState<IDefaultRouteCostsData>(initialForm);
  const [errors, setErrors] = useState<ValidationErrors>({ costs: [] });

  useEffect(() => {
    setErrors({ costs: [] }); // Limpa os erros ao abrir novo formulário
  }, [initialForm]);

  // Obtém os tipos de veículos disponíveis para um formulário específico
  const getAvailableVehicleTypes = (currentItemIndex: number) => {
    const usedTypes = new Set(
      form.costs
        .filter((_, index) => index !== currentItemIndex)
        .map(item => item.vehicleTypeId)
    );
    
    return vehicleTypes.filter(type => {
      // Se for o veículo atual, sempre inclui
      if (currentItemIndex >= 0 && form.costs[currentItemIndex]?.vehicleTypeId === Number(type.id)) {
        return true;
      }
      // Caso contrário, só inclui se não estiver sendo usado
      return !usedTypes.has(Number(type.id));
    });
  };

  // Adicionar uma nova linha de item
  const handleAddItem = () => {
    const availableTypes = getAvailableVehicleTypes(-1);
    
    if (availableTypes.length > 0) {
      const newForm = { ...form };
      const lastItem = newForm.costs[newForm.costs.length - 1];
      const initialCapacity = lastItem ? lastItem.finalCapacity + 1 : 0;
      
      newForm.costs.push({
        vehicleTypeId: Number(availableTypes[0].id),
        initialCapacity,
        finalCapacity: 0,
        cost: 0,
        leaderCommission: 0,
        additionalDelivery: 0,
        unloadingCost: null,
      });
      setForm(newForm);
    } else {
      toast.error("Não há mais tipos de veículo disponíveis para adicionar");
    }
  };

  // Remover um item específico
  const handleRemoveItem = (itemIndex: number) => {
    const newForm = { ...form };
    newForm.costs.splice(itemIndex, 1);
    setForm(newForm);
    
    const newErrors = { ...errors };
    newErrors.costs.splice(itemIndex, 1);
    setErrors(newErrors);
  };

  // Atualizar valores de um item
  const handleItemChange = (
    itemIndex: number,
    field: 'vehicleTypeId' | 'initialCapacity' | 'finalCapacity' | 'cost' | 'additionalDelivery',
    value: number | string
  ) => {
    const newForm = { ...form };
    
    newForm.costs[itemIndex][field] = Number(value);
    
    setForm(newForm);
    
    if (errors.costs[itemIndex]?.[field as keyof typeof errors.costs[number]]) {
      const newErrors = { ...errors };
      newErrors.costs[itemIndex] = {
        ...newErrors.costs[itemIndex],
        [field]: false
      };
      
      if (field === 'initialCapacity' || field === 'finalCapacity') {
        newErrors.costs[itemIndex].overlapError = false;
      }
      
      setErrors(newErrors);
    }
  };

  // Atualizar o centro de distribuição
  const handleTransshipPointChange = (value: string) => {
    const newForm = { ...form, transshipPointId: Number(value) };
    setForm(newForm);
    
    if (errors.transshipPointId) {
      const newErrors = { ...errors, transshipPointId: false };
      setErrors(newErrors);
    }
  };
    
  // Atualizar o centro de distribuição quando houver apenas um centro de distribuição
  useEffect(() => {
    if (transshipPoints.length === 1) {
      setForm({ ...form, transshipPointId: transshipPoints[0].id });
    }
  }, [transshipPoints]);

  // Salvar o formulário
  const handleSave = () => {
    const { isValid, errors: validationErrors } = validateForm(form);
    setErrors(validationErrors);
    if (!isValid) return;
    onSave(form);
  };

  return (
    <Box px={0} py={2} width="100%" style={{ background: '#fff', borderRadius: 8 }}>
      <Box sx={{ maxWidth: 400, mb: 2 }}>
        <FormControl fullWidth size="medium">
          <Typography variant="subtitle1" gutterBottom>
            Centro de Distribuição
          </Typography>
          <Select
            value={form.transshipPointId.toString()}
            onChange={(e) => handleTransshipPointChange(e.target.value)}
            disabled={loading}
            error={!!errors.transshipPointId}
            size="medium"
          >
            {transshipPoints.map((center) => (
              <MenuItem key={center.id} value={center.id}>
                {center.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    
      {form.costs.map((item, itemIndex) => (
        <CostLineCard
          key={itemIndex}
          item={item}
          itemIndex={itemIndex}
          errors={errors.costs[itemIndex]}
          loading={loading}
          isEditing={true}
          onChange={(itemIndex, field, value) => handleItemChange(itemIndex, field as any, value)}
          onRemove={handleRemoveItem}
          getAvailableVehicleTypes={getAvailableVehicleTypes}
          disableRemove={form.costs.length <= 1}
        />
      ))}
      <Box display="flex" justifyContent="flex-end" gap={[1, 3]} mt={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
        <NewButton 
          variantType="outlined" 
          onClick={handleAddItem} 
          disabled={loading || getAvailableVehicleTypes(-1).length === 0}
          startIcon={<AddIcon />}
        >
          Adicionar veículo
        </NewButton>
        <NewButton variantType="filled" onClick={handleSave} disabled={loading}>
          Salvar
        </NewButton>
        <NewButton variantType="cancel" onClick={onRemove} disabled={loading}>
          Cancelar
        </NewButton>
      </Box>
    </Box>
  );
};

export default NewDefaultCostForm; 